import { CalendarClock, Check, UserX } from "lucide-react";
import { CrmTaskActionLink } from "@/components/crm/task-guidance-panels";
import { cn } from "@/lib/utils";
import type { CrmTaskAction } from "@ease/crm/task-action-groups";

interface CrmTaskCompletionToggleButtonProps {
  isCompleted: boolean;
  isClosed: boolean;
  onComplete: () => void;
}

interface CrmTaskRowActionsProps {
  quickActions: readonly CrmTaskAction[];
  hasAssignee: boolean;
  isClosed: boolean;
  isClearingAssignee: boolean;
  isRescheduling: boolean;
  expanded: boolean;
  detailsPanelId: string;
  detailsToggleLabel: string;
  onClearAssignee: () => void;
  onRescheduleInDays: (days: number) => void;
  onComplete: () => void;
  onToggleDetails: () => void;
  onSkip: () => void;
}

export function CrmTaskCompletionToggleButton({
  isCompleted,
  isClosed,
  onComplete,
}: CrmTaskCompletionToggleButtonProps) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        if (!isClosed) onComplete();
      }}
      disabled={isClosed}
      title={isClosed ? undefined : "Complete task with outcome"}
      className={cn(
        "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
        isCompleted
          ? "border-[var(--color-success)] bg-[var(--color-success)] text-[var(--color-primary-foreground)]"
          : isClosed
            ? "border-[var(--color-border)] text-[var(--color-muted-foreground)] opacity-60"
            : "border-[var(--color-border)] hover:border-[var(--color-primary)]",
      )}
      aria-label={isCompleted ? "Task completed" : "Complete task with outcome"}
    >
      {isCompleted && <Check className="h-3 w-3" />}
    </button>
  );
}

export function CrmTaskRowActions({
  quickActions,
  hasAssignee,
  isClosed,
  isClearingAssignee,
  isRescheduling,
  expanded,
  detailsPanelId,
  detailsToggleLabel,
  onClearAssignee,
  onRescheduleInDays,
  onComplete,
  onToggleDetails,
  onSkip,
}: CrmTaskRowActionsProps) {
  return (
    <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
      {quickActions.map((action) => (
        <CrmTaskActionLink key={`quick:${action.key}`} action={action} compact />
      ))}
      {hasAssignee && !isClosed && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onClearAssignee();
          }}
          disabled={isClearingAssignee}
          className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] px-2 py-1 text-[11px] font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)] disabled:cursor-not-allowed disabled:opacity-60"
          title="Move task back to the unassigned queue"
          aria-label="Move task back to unassigned queue"
        >
          <UserX className="h-3 w-3" />
          {isClearingAssignee ? "Unassigning" : "Unassign"}
        </button>
      )}
      {!isClosed && (
        <>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onRescheduleInDays(1);
            }}
            disabled={isRescheduling}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] px-2 py-1 text-[11px] font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)] disabled:cursor-not-allowed disabled:opacity-60"
            title="Defer task to tomorrow"
            aria-label="Defer task to tomorrow"
          >
            <CalendarClock className="h-3 w-3" />
            Tomorrow
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onRescheduleInDays(7);
            }}
            disabled={isRescheduling}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] px-2 py-1 text-[11px] font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)] disabled:cursor-not-allowed disabled:opacity-60"
            title="Defer task to next week"
            aria-label="Defer task to next week"
          >
            <CalendarClock className="h-3 w-3" />
            Next week
          </button>
        </>
      )}
      {!isClosed && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onComplete();
          }}
          className="rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-xs font-semibold text-[var(--color-primary-foreground)] shadow-sm hover:bg-[var(--color-primary-hover)]"
          aria-label="Complete task with outcome"
        >
          Complete with outcome
        </button>
      )}
      <button
        type="button"
        onClick={onToggleDetails}
        className="rounded-md border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)] hover:text-[var(--color-foreground)]"
        aria-expanded={expanded}
        aria-controls={detailsPanelId}
        aria-label={detailsToggleLabel}
      >
        {expanded ? "Hide details" : "Open details"}
      </button>
      {!isClosed && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onSkip();
          }}
          className="rounded-md border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)] hover:text-[var(--color-foreground)]"
          aria-label="Mark task as no longer needed"
        >
          No longer needed
        </button>
      )}
    </div>
  );
}
